// GA4 first-party analytics — production-only, consent-gated, page_view only.
// No Google Signals, no ad personalization, no User-ID, no PII.
// Build marker: 2026-09-16 rebuild to re-inline REACT_APP_GA4_MEASUREMENT_ID.

export const PRODUCTION_HOSTS = ["trendriseai.com", "www.trendriseai.com"];

export const SENSITIVE_PARAMS = [
  "code", "state", "token", "access_token", "refresh_token", "jwt",
  "session", "secret", "password", "client_secret",
];

const SENSITIVE_PARAM_RE = /(token|secret|password|session|jwt|code|state|key|auth|email)/i;

export const CONSENT_KEY = "trendrise_analytics_consent";
export const CONSENT_EVENT = "trendrise:analytics-consent";
const GRANTED = "granted";
const DENIED = "denied";

let initialized = false;
let activeMeasurementId = "";
let lastPageLocation = null;
let runtimeMeasurementId = "";

const ID_RE = /^G-[A-Z0-9]{6,12}$/i;

function storage(win) {
  try { return win && win.localStorage; } catch { return null; }
}

// Build-time value (CRA inlines REACT_APP_*) wins; otherwise the runtime value
// fetched from /api/config/public (backend reads it from its own environment).
export function getMeasurementId(env = process.env) {
  const id = (env.REACT_APP_GA4_MEASUREMENT_ID || "").trim();
  if (ID_RE.test(id)) return id;
  return ID_RE.test(runtimeMeasurementId) ? runtimeMeasurementId : "";
}

export function setRuntimeMeasurementId(id) {
  const v = String(id || "").trim();
  runtimeMeasurementId = ID_RE.test(v) ? v : "";
  return runtimeMeasurementId;
}

export function isProductionHost(hostname) {
  return PRODUCTION_HOSTS.includes((hostname || "").toLowerCase());
}

// ---------------- consent (first-party localStorage, choice only) ----------------

export function getConsent(win = typeof window !== "undefined" ? window : undefined) {
  const s = storage(win);
  if (!s) return null;
  try {
    const v = s.getItem(CONSENT_KEY);
    return v === GRANTED || v === DENIED ? v : null;
  } catch { return null; }
}

export function hasAnalyticsConsent(win = typeof window !== "undefined" ? window : undefined) {
  return getConsent(win) === GRANTED;
}

export function setConsent(choice, win = typeof window !== "undefined" ? window : undefined) {
  if (choice !== GRANTED && choice !== DENIED) return;
  const s = storage(win);
  try { s && s.setItem(CONSENT_KEY, choice); } catch { /* storage unavailable */ }
  if (choice === DENIED) withdrawAnalytics(win);
  try { win && win.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { choice } })); } catch { /* non-DOM */ }
}

export function clearAnalyticsCookies(win = typeof window !== "undefined" ? window : undefined, doc = typeof document !== "undefined" ? document : undefined) {
  if (!doc || typeof doc.cookie !== "string") return [];
  const hostname = (win && win.location && win.location.hostname) || "";
  const parts = hostname.split(".");
  const domains = [""];
  for (let i = 0; i < parts.length - 1; i += 1) domains.push(parts.slice(i).join("."));
  const removed = [];
  for (const raw of doc.cookie.split(";")) {
    const name = raw.split("=")[0].trim();
    if (!/^_ga($|_)|^_gid$|^_gat/.test(name)) continue;
    for (const d of domains) {
      const domainAttr = d ? `; domain=${d}` : "";
      doc.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainAttr}`;
    }
    removed.push(name);
  }
  return removed;
}

export function withdrawAnalytics(win = typeof window !== "undefined" ? window : undefined) {
  if (win && activeMeasurementId) win[`ga-disable-${activeMeasurementId}`] = true;
  lastPageLocation = null;
  return clearAnalyticsCookies(win);
}

// ---------------- gating ----------------

export function shouldTrack({ measurementId, hostname, nodeEnv, consent }) {
  return Boolean(measurementId) && nodeEnv === "production" && isProductionHost(hostname) && consent === true;
}

export function sanitizeUrl(input) {
  try {
    const url = new URL(input, "https://trendriseai.com");
    for (const key of Array.from(url.searchParams.keys())) {
      if (SENSITIVE_PARAMS.includes(key.toLowerCase()) || SENSITIVE_PARAM_RE.test(key)) {
        url.searchParams.delete(key);
      }
    }
    url.hash = "";
    return url.toString();
  } catch {
    return "";
  }
}

export function sanitizePath(pathname, search) {
  const sanitized = sanitizeUrl(`${pathname || "/"}${search || ""}`);
  if (!sanitized) return "/";
  const u = new URL(sanitized);
  return `${u.pathname}${u.search}`;
}

export function resetForTests() {
  initialized = false;
  activeMeasurementId = "";
  lastPageLocation = null;
  runtimeMeasurementId = "";
}

export function isInitialized() {
  return initialized;
}

// ---------------- init / track ----------------

export function initGA4({
  measurementId = getMeasurementId(),
  hostname = typeof window !== "undefined" ? window.location.hostname : "",
  nodeEnv = process.env.NODE_ENV,
  win = typeof window !== "undefined" ? window : undefined,
  doc = typeof document !== "undefined" ? document : undefined,
  consent = hasAnalyticsConsent(win),
} = {}) {
  if (!win || !doc || !shouldTrack({ measurementId, hostname, nodeEnv, consent })) return false;
  if (initialized) {
    win[`ga-disable-${measurementId}`] = false;
    return true;
  }

  win.dataLayer = win.dataLayer || [];
  win.gtag = win.gtag || function gtag() { win.dataLayer.push(arguments); };

  const script = doc.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  doc.head.appendChild(script);

  win.gtag("js", new Date());
  win.gtag("config", measurementId, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    anonymize_ip: true,
  });
  initialized = true;
  activeMeasurementId = measurementId;
  return true;
}

export function trackPageView({
  pathname,
  search = "",
  title = "",
  origin = typeof window !== "undefined" ? window.location.origin : "https://trendriseai.com",
  win = typeof window !== "undefined" ? window : undefined,
  consent = hasAnalyticsConsent(win),
} = {}) {
  if (!initialized || !win || typeof win.gtag !== "function" || consent !== true) return false;
  if (win[`ga-disable-${activeMeasurementId}`] === true) return false;
  const page_path = sanitizePath(pathname, search);
  const page_location = sanitizeUrl(`${origin}${page_path}`);
  if (!page_location || page_location === lastPageLocation) return false;
  lastPageLocation = page_location;
  win.gtag("event", "page_view", {
    page_location,
    page_path,
    page_title: String(title || "").slice(0, 200),
  });
  return true;
}
